import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import React, { memo, type ComponentRef } from "react";

import { boardCollisionDetection } from "../../utils/boardCollisionDetection";
import { ActiveDragProvider } from "../../contexts/ActiveDragContext";
import type { ReopenStageOption } from "../../components/molecules/ReopenCardDialog";
import ClosedArea from "@/features/player-kanban/components/organisms/ClosedArea";
import AddColumnButton from "@/features/player-kanban/components/molecules/AddColumnButton";
import AddPlayersPanel from "@/features/player-kanban/components/organisms/AddPlayersPanel";
import type { BoardInfo } from "@/features/player-kanban/components/organisms/BoardTabs/BoardTabsContainer";
import KanbanColumnWrapper from "@/features/player-kanban/components/organisms/KanbanColumnWrapper";
import PipelineActivityLogDrawer from "@/features/player-kanban/components/organisms/PipelineActivityLogDrawer";
import PipelineTable from "@/features/player-kanban/components/organisms/PipelineTable";
import PlayerSidebar from "@/features/player-kanban/components/organisms/PlayerSidebar";
import ReviewQueueDrawer from "@/features/player-kanban/components/organisms/ReviewQueueDrawer";
import RulesPanel from "@/features/player-kanban/components/organisms/RulesPanel";
import StageEditor from "@/features/player-kanban/components/organisms/StageEditor";
import TopPanel from "@/features/player-kanban/components/organisms/TopPanel";
import type {
  BoardSortDirection,
  BoardSortOption,
  BoardSortState,
} from "../../constants/boardSort";
import type { FilterState } from "../../constants/filterDefaults";
import type { ServerGpvRange } from "../../utils/playerFilter";
import type {
  ActiveDragState,
  BoardViewMode,
  CreatePlayerDropHandler,
  DragSensors,
  RemovePlayerHandler,
  ScrollViewLayoutHandler,
} from "../../types/types";
import { ClosedGroupData, ColumnData } from "../../types/types";

import KanbanPlayerCard from "@/components/organisms/custom/ui/KanbanPlayerCard";
import SidebarDragCard from "@/components/organisms/custom/ui/SidebarDragCard";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { ScrollView } from "@/components/atoms/ScrollView";
import {
  KanbanPipelineTemplate,
  UserKanbanBoardRole,
} from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

/**
 * Imperative handle of the horizontal-columns ScrollView atom, shared with the
 * container that owns the ref. Derived from the atom so app code never
 * references react-native's ScrollView type directly.
 */
export type KanbanScrollViewRef = ComponentRef<typeof ScrollView>;

/**
 * No-op function for overlay card press handler
 */
const noopPress = () => {};

/**
 * DragOverlay style (dnd-kit) — max z-index so the dragged card floats above all.
 * design-system: dynamic-style waiver — supra-token overlay z-index + pointer passthrough
 */
const DRAG_OVERLAY_STYLE = {
  zIndex: 2147483647,
  pointerEvents: "none" as const,
};

/**
 * Lift applied to the floating card under the cursor (SE-6554, Figma 80:16392 —
 * "Card · Drag ghost (under cursor)").
 *
 * The tilt is what separates "a card is being carried" from "a card is stuck to
 * the pointer": it breaks alignment with the lanes underneath, so the overlay
 * never reads as a card that merely failed to move. The design specifies the
 * direction and the amount — −3°, counter-clockwise — where dev had guessed +2°.
 *
 * The scale-up and the 90% opacity dev had here are gone: the design's ghost is
 * a full-opacity card at its own size, and the two of them together were what
 * made the ghost read as a different, hazier object than the card that had just
 * been picked up. The shadow does the lifting on its own now — `shadow-soft-2`
 * is the closed ladder's stand-in for the design's shadow-lg.
 *
 * Scoped to `web:` because `DragOverlay` is a dnd-kit web primitive — this
 * branch never renders on native.
 */
const DRAG_OVERLAY_CARD_CLASS = "web:-rotate-3 rounded-md shadow-soft-2";

/**
 * Props for MainView component
 */
interface MainViewProps {
  columnsData: ColumnData[];
  /**
   * SE-5491: the Table renderer's dataset — the same `columnsData` plus the
   * defensive unstaged bucket. Both come from the one `useKanbanBoard`
   * derivation, so the two renderers can never diverge.
   */
  tableColumnsData?: ColumnData[];
  /**
   * SE-5496: closed cards grouped by outcome for the collapsed Closed area,
   * rendered at the END of the board.
   */
  closedGroups?: ClosedGroupData[];
  /** SE-5496: working (non-terminal) stages a closed card may be reopened into. */
  workingStages?: ReopenStageOption[];
  /** SE-5496: reopen a closed card into a working stage. */
  onReopenCard?: (
    cardId: string,
    targetPhaseId: string,
    rationale?: string
  ) => Promise<void>;
  /** SE-5491: which renderer is mounted. Defaults to the kanban board. */
  viewMode?: BoardViewMode;
  /** SE-5491: fired with the newly selected renderer. */
  onViewModeChange?: (mode: BoardViewMode) => void;
  /** SE-7144: opens the TABLE view's Watchlist-parity filter drawer. */
  onTableFiltersPress?: () => void;
  /** SE-7144: committed filter count for the Table view's Filters badge. */
  tableActiveFilterCount?: number;
  /** SE-7405: opens the KANBAN view's Watchlist-parity filter drawer. */
  onKanbanFiltersPress?: () => void;
  /** SE-7405: committed filter count for the Kanban view's Filters badge. */
  kanbanActiveFilterCount?: number;
  boardName?: string;
  boardId?: string;
  boards?: BoardInfo[];
  /** SE-6575: whether boards exist beyond those loaded (page cap / failed page). */
  hasUnreachableBoards?: boolean;
  isRosterBoard?: boolean;
  /**
   * SE-5489: whether the active surface is the migrated PIPELINE surface
   * (SE-6071 flag on + Manager+). Drives the TopPanel center fork — the
   * pipeline chip switcher when true, the legacy BoardTabs strip otherwise.
   */
  isPipelineSurface?: boolean;
  /**
   * SE-5490: the open board's recruitment motion. It labels the pipeline and
   * seeds the stage glossary; it does NOT narrow the add-players source tabs,
   * which every pipeline offers in full (SE-6913). `null`/`undefined` is a
   * migrated board and is meaningful, not merely missing.
   */
  pipelineTemplate?: KanbanPipelineTemplate | null;
  playerCount?: number;
  boardFilters?: FilterState;
  /** SE-6488: board-scoped player search text, driven by the header toolbar. */
  boardSearchQuery?: string;
  /** SE-6488: fired as the user types in the header toolbar's search input. */
  onBoardSearchChange?: (value: string) => void;
  /** SE-6488: the board-level sort selection. */
  boardSort?: BoardSortState;
  /** SE-6488: fired with the newly selected board sort field. */
  onBoardSortChange?: (option: BoardSortOption) => void;
  /** SE-6554: fired with the newly selected board sort direction. */
  onBoardSortDirectionChange?: (direction: BoardSortDirection) => void;
  /**
   * Active GPV bounds (euros) for the server-side watchlist filter (SE-6496).
   * Null while the cost filter is inactive.
   */
  serverGpvRange?: ServerGpvRange | null;
  isGloballyDragging: boolean;
  dragSource?: "sidebar" | "kanban" | null;
  scrollViewRef: React.MutableRefObject<KanbanScrollViewRef | null>;
  kanbanCards?: {
    player: { id: string };
    kanbanPhase: { id: string; name: string };
  }[];
  boardRefreshTrigger?: number;
  sensors: DragSensors;
  active: ActiveDragState;
  canShare?: boolean;
  canEdit?: boolean;
  isOwner?: boolean;
  userRole?: UserKanbanBoardRole | null;
  onAddColumn: () => void;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
  onDragOver: (event: DragOverEvent) => void;
  onUpdateBoardName?: (newName: string) => Promise<void>;
  onBoardSelect?: (boardId: string) => void;
  onCreateBoard?: () => void;
  onMoreBoardOptions?: () => void;
  onDeleteBoard?: (boardId: string) => Promise<void>;
  onGlobalDragStart?: (dragData?: unknown, event?: unknown) => void;
  onGlobalDragEnd?: () => void;
  onSidebarDragStart?: (dragData?: unknown, event?: unknown) => void;
  onKanbanDragStart?: (dragData?: unknown, event?: unknown) => void;
  onRemovePlayerFromBoard?: RemovePlayerHandler;
  onTagAdded?: (playerId: string, tag: string) => void;
  getPlayerTags?: (playerId: string) => string[];
  onAutoScroll: (dragX: number) => void;
  onScrollViewLayout: ScrollViewLayoutHandler;
  createColumnTitleHandler: (index: number) => (newTitle: string) => void;
  createPlayerDropHandler: CreatePlayerDropHandler;
  createColumnDeleteHandler: (columnId: string) => () => void;
  onPlayerClick?: (playerId: string) => void;
  onBoardRefresh?: () => void;
  onSharePress?: () => void;
}

/**
 * MainView Component - Pure UI component for player kanban main layout with drag-and-drop functionality
 * @param root0 - The component props
 * @param root0.columnsData - Array of column data containing player information for each kanban column
 * @param root0.boardName - Name of the current kanban board (optional)
 * @param root0.boardId - Unique identifier for the current board (optional)
 * @param root0.boards - List of available boards for board selection (optional)
 * @param root0.hasUnreachableBoards - Whether boards exist beyond those loaded (SE-6575)
 * @param root0.boardFilters - Active filter settings for the board including positions, age, GPR, etc (optional)
 * @param root0.boardSearchQuery - Board-scoped player search text (SE-6488)
 * @param root0.onBoardSearchChange - Fired as the user types in board search (SE-6488)
 * @param root0.boardSort - The board-level sort selection (SE-6488)
 * @param root0.onBoardSortChange - Fired with the new board sort field (SE-6488)
 * @param root0.onBoardSortDirectionChange - Fired with the new board sort direction (SE-6554)
 * @param root0.serverGpvRange - Active GPV bounds (euros) for the server-side watchlist filter (SE-6496); null when inactive
 * @param root0.isGloballyDragging - Whether a drag operation is currently active anywhere in the board
 * @param root0.dragSource - Source of the current drag operation (sidebar, kanban, or null) (optional)
 * @param root0.scrollViewRef - Ref to the scroll view for programmatic scrolling
 * @param root0.kanbanCards - Array of kanban card data linking players to phases (optional)
 * @param root0.boardRefreshTrigger - Trigger value to force board refresh (optional)
 * @param root0.sensors - DnD Kit sensors configuration for drag interactions
 * @param root0.active - Currently active draggable item from DnD Kit
 * @param root0.canShare - Whether the current user can share the board (optional)
 * @param root0.canEdit - Whether the current user can edit the board (optional)
 * @param root0.isOwner - Whether the current user owns the board (optional)
 * @param root0.userRole - Current user's role on the board (optional)
 * @param root0.onAddColumn - Callback to add a new column to the board
 * @param root0.onDragStart - Callback invoked when a drag operation starts
 * @param root0.onDragEnd - Callback invoked when a drag operation ends
 * @param root0.onDragCancel - Callback invoked when a drag operation is cancelled
 * @param root0.onDragOver - Callback invoked when dragging over a drop target
 * @param root0.onUpdateBoardName - Callback to update the board name (optional)
 * @param root0.onBoardSelect - Callback when a different board is selected (optional)
 * @param root0.onCreateBoard - Callback to create a new board (optional)
 * @param root0.onMoreBoardOptions - Callback to show more board options menu (optional)
 * @param root0.onDeleteBoard - Callback to delete a board (optional)
 * @param root0.onGlobalDragStart - Callback when any drag starts globally (optional)
 * @param root0.onGlobalDragEnd - Callback when any drag ends globally (optional)
 * @param root0.onSidebarDragStart - Callback when drag starts from sidebar (optional)
 * @param root0.onKanbanDragStart - Callback when drag starts from kanban board (optional)
 * @param root0.onRemovePlayerFromBoard - Callback to remove a player from the board (optional)
 * @param root0.onTagAdded - Callback when a tag is added to a player (optional)
 * @param root0.getPlayerTags - Function to retrieve tags for a specific player (optional)
 * @param root0.onAutoScroll - Callback to handle automatic scrolling during drag operations
 * @param root0.onScrollViewLayout - Callback when scroll view layout changes
 * @param root0.createColumnTitleHandler - Factory function to create title change handlers for columns
 * @param root0.createPlayerDropHandler - Factory function to create drop handlers for columns
 * @param root0.createColumnDeleteHandler - Factory function to create delete handlers for columns
 * @param root0.onPlayerClick - Callback when a player card is clicked (optional)
 * @param root0.onBoardRefresh - Callback to refresh board data (optional)
 * @param root0.onSharePress - Callback when share button is pressed (optional)
 * @param root0.isRosterBoard - Whether this is a roster board
 * @param root0.isPipelineSurface - Whether the active surface is the PIPELINE surface (SE-5489/SE-6071); forks the TopPanel center between the chip switcher and BoardTabs
 * @param root0.pipelineTemplate - The open board's recruitment motion (SE-5490); labels the pipeline and seeds the stage glossary
 * @param root0.playerCount - Current number of players on the board
 * @param root0.tableColumnsData - Dataset for the Table renderer (SE-5491)
 * @param root0.closedGroups - Closed cards grouped by outcome for the Closed area (SE-5496)
 * @param root0.workingStages - Working stages a closed card may be reopened into (SE-5496)
 * @param root0.onReopenCard - Reopen a closed card into a working stage (SE-5496)
 * @param root0.viewMode - Which renderer is mounted (SE-5491)
 * @param root0.onViewModeChange - Callback fired with the newly selected renderer (SE-5491)
 * @param root0.onTableFiltersPress - Opens the TABLE view's Watchlist-parity filter drawer (SE-7144)
 * @param root0.tableActiveFilterCount - Committed filter count for the Table view's Filters badge (SE-7144)
 * @param root0.onKanbanFiltersPress - Opens the KANBAN view's Watchlist-parity filter drawer (SE-7405)
 * @param root0.kanbanActiveFilterCount - Committed filter count for the Kanban view's Filters badge (SE-7405)
 */
const MainView = ({
  columnsData,
  tableColumnsData,
  closedGroups,
  workingStages,
  onReopenCard,
  viewMode = "kanban",
  onViewModeChange,
  onTableFiltersPress,
  tableActiveFilterCount,
  onKanbanFiltersPress,
  kanbanActiveFilterCount,
  boardName,
  boardId,
  boards,
  hasUnreachableBoards,
  isRosterBoard,
  isPipelineSurface,
  pipelineTemplate,
  playerCount,
  boardFilters,
  boardSearchQuery,
  onBoardSearchChange,
  boardSort,
  onBoardSortChange,
  onBoardSortDirectionChange,
  serverGpvRange,
  isGloballyDragging,
  dragSource,
  scrollViewRef,
  kanbanCards,
  boardRefreshTrigger,
  sensors,
  active,
  canShare,
  canEdit,
  isOwner,
  userRole,
  onAddColumn,
  onDragStart,
  onDragEnd,
  onDragCancel,
  onDragOver,
  onUpdateBoardName,
  onBoardSelect,
  onCreateBoard,
  onMoreBoardOptions,
  onDeleteBoard,
  onGlobalDragStart: _onGlobalDragStart,
  onGlobalDragEnd,
  onSidebarDragStart,
  onKanbanDragStart,
  onRemovePlayerFromBoard,
  onTagAdded,
  getPlayerTags,
  onAutoScroll,
  onScrollViewLayout,
  createColumnTitleHandler,
  createPlayerDropHandler,
  createColumnDeleteHandler,
  onPlayerClick,
  onBoardRefresh,
  onSharePress,
}: MainViewProps) => (
  <Box
    {...ifWeb({ id: "kanban-root" }, { nativeID: "kanban-root" })}
    className="flex h-full flex-1 flex-col"
  >
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      onDragOver={onDragOver}
    >
      <ActiveDragProvider value={active ? String(active.id) : null}>
        <Box className="relative isolation-auto z-auto flex flex-1 flex-col overflow-visible bg-surface-base">
          {/* Top Panel */}
          <TopPanel
            boardId={boardId}
            boardName={boardName}
            boards={boards}
            hasUnreachableBoards={hasUnreachableBoards}
            isRosterBoard={isRosterBoard}
            isPipelineSurface={isPipelineSurface}
            pipelineTemplate={pipelineTemplate}
            playerCount={playerCount}
            boardSearchQuery={boardSearchQuery}
            onBoardSearchChange={onBoardSearchChange}
            boardSort={boardSort}
            onBoardSortChange={onBoardSortChange}
            onBoardSortDirectionChange={onBoardSortDirectionChange}
            onUpdateBoardName={onUpdateBoardName}
            onBoardSelect={onBoardSelect}
            onCreateBoard={onCreateBoard}
            onMoreBoardOptions={onMoreBoardOptions}
            onDeleteBoard={onDeleteBoard}
            canEdit={canEdit}
            isOwner={isOwner}
            canShare={canShare}
            userRole={userRole}
            onSharePress={onSharePress}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            onTableFiltersPress={onTableFiltersPress}
            tableActiveFilterCount={tableActiveFilterCount}
            onKanbanFiltersPress={onKanbanFiltersPress}
            kanbanActiveFilterCount={kanbanActiveFilterCount}
          />

          {/* SE-5499: pipeline Review-queue drawer. Mounted once; opened via the
              TopPanel's Review-queue button (which drives the reactive var). */}
          <ReviewQueueDrawer />

          {/* SE-5504: the per-pipeline, manager-facing activity & audit log.
              Mounted once; opened via the TopPanel's Activity button. Its read
              is skipped while closed, so mounting it costs nothing. */}
          <PipelineActivityLogDrawer />

          {/* SE-5506: pipeline Rules & automation panel. Mounted once; opened via
              the TopPanel's Rules button (which drives the reactive var). */}
          <RulesPanel />

          {/* SE-5488: pipeline Stage editor (rename, reorder, add, remove +
              protected/terminal flags). Mounted once; opened via the TopPanel's
              Stages button (which drives the reactive var). `activeBoardId` lets
              the editor render closed if the board is switched while it is open,
              so it never operates on the stale board. */}
          <StageEditor
            activeBoardId={boardId}
            pipelineTemplate={pipelineTemplate}
          />

          {/* Main Content Area */}
          <Stack
            direction="horizontal"
            className="relative isolation-auto z-auto h-full flex-1 overflow-visible"
          >
            {/* SE-5490: the left rail forks on the SURFACE, not on the board.
                The pipeline surface gets the add-players panel (contextual
                source tabs incl. Suggested, persistent global search, collapse
                default); every legacy watchlist/roster board keeps the
                unchanged PlayerSidebar, so this ticket cannot regress the
                surface that is live today. Both rails register the same
                `sidebar-removal-zone` droppable, so drag-to-remove is
                identical either way. */}
            {isPipelineSurface ? (
              <AddPlayersPanel
                kanbanBoardId={boardId}
                boardName={boardName}
                pipelineTemplate={pipelineTemplate}
                kanbanCards={kanbanCards}
                isGloballyDragging={isGloballyDragging}
                dragSource={dragSource}
                boardFilters={boardFilters}
                serverGpvRange={serverGpvRange}
                onGlobalDragStart={onSidebarDragStart}
                onGlobalDragEnd={onGlobalDragEnd}
                onRemovePlayerFromBoard={onRemovePlayerFromBoard}
                onAutoScroll={onAutoScroll}
              />
            ) : (
              <PlayerSidebar
                isGloballyDragging={isGloballyDragging}
                dragSource={dragSource}
                kanbanCards={kanbanCards}
                boardFilters={boardFilters}
                serverGpvRange={serverGpvRange}
                onGlobalDragStart={onSidebarDragStart}
                onGlobalDragEnd={onGlobalDragEnd}
                onRemovePlayerFromBoard={onRemovePlayerFromBoard}
                onAutoScroll={onAutoScroll}
              />
            )}

            {/* SE-5491 branch point: one dataset, two renderers. The Table
                consumes `tableColumnsData` — the same `useKanbanBoard`
                `columnsData` derivation the Kanban columns below render, plus
                the defensive unstaged bucket — so toggling can never change
                which cards sit in which stages. Branching here (rather than
                inside KanbanColumnContainer) is what keeps the shared hook the
                single source; the ticket's "extend KanbanColumnContainer, do
                not fork a parallel kanban implementation" forbids a SECOND
                kanban, not a sibling renderer. */}
            {viewMode === "table" ? (
              <PipelineTable
                columnsData={tableColumnsData ?? columnsData}
                boardId={boardId}
                canEdit={canEdit}
                onPlayerClick={onPlayerClick}
              />
            ) : (
              /* Kanban Board Container */
              /* overflow-hidden enables proper scroll clipping */
              /* SE-6554 item K: the 32px page gutter lives HERE, on the
                 unscaled outer box, not on the scrolling content below. The
                 content container is CSS `scale(0.8)` on web, so a 32 written
                 inside it renders 25.6 while the TopPanel's px-8 renders a true
                 32 — the few-pixel misalignment between the first lane and the
                 search input's left border. Hoisting the inset out of the
                 scaled node makes both edges exactly 32 from the same origin,
                 and it no longer depends on the scale factor. */
              <Box className="relative isolation-auto z-auto flex-1 overflow-hidden bg-surface-base px-8 pb-2 pt-4">
                <ScrollView
                  ref={scrollViewRef}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{
                    /* Lane rhythm per Figma 80:15664 — the lanes row places its
                       264px columns on a 278px pitch (a 14px gutter) with no
                       padding of its own. 14 is off the closed spacing ladder;
                       16 is the nearest rung and the proportional match for
                       this board's wider 288px (w-72) column.
                       The board container's 32px horizontal padding (Figma
                       80:15014) is applied on the UNSCALED wrapper above, not
                       here — see the item-K note there. Keep this at 0. */
                    flexDirection: "row" as const,
                    gap: 16,
                    paddingHorizontal: 0,
                    minHeight: ifWeb("125%" as const, undefined), // Web only: compensate for 0.8 scale
                    ...ifWeb(
                      {
                        transform: "scale(0.8)" as const,
                        transformOrigin: "top left" as const,
                      },
                      {}
                    ),
                  }}
                  className="web:overflow-scroll flex-1"
                  // These props help with drag overlay visibility
                  removeClippedSubviews={false}
                  onLayout={onScrollViewLayout}
                >
                  <SortableContext
                    items={columnsData.map(col => col.id).filter(Boolean)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {columnsData.map((column, index) => (
                      <KanbanColumnWrapper
                        key={column.id}
                        columnId={column.id}
                        title={column.title}
                        count={column.count}
                        players={column.players}
                        isEmpty={column.isEmpty}
                        emptyText={column.emptyText}
                        boardId={boardId}
                        boardRefreshTrigger={boardRefreshTrigger}
                        isGloballyDragging={isGloballyDragging}
                        canEdit={canEdit ?? false}
                        canDelete={canEdit ?? false}
                        onTitleChange={createColumnTitleHandler(index)}
                        onPlayerDrop={createPlayerDropHandler(column.id)}
                        onPlayerClick={onPlayerClick}
                        onGlobalDragStart={onKanbanDragStart}
                        onGlobalDragEnd={onGlobalDragEnd}
                        onAutoScroll={onAutoScroll}
                        onTagAdded={onTagAdded}
                        getPlayerTags={getPlayerTags}
                        onBoardRefresh={onBoardRefresh}
                        onDelete={createColumnDeleteHandler(column.id)}
                      />
                    ))}
                  </SortableContext>

                  {/* Collapsed Closed area, grouped by outcome. Renders nothing
                      when no card is closed.

                      SE-5496 introduced it as the LAST child of the strip, on
                      the reading that closed history belongs after everything
                      else. SE-6861 (Sprint 18 staging QA) reverses that pair on
                      purpose: Closed is a non-sortable summary of the stages, so it
                      now trails the stages directly, and "Add column" — the one
                      control that creates a new stage — is what ends the board.

                      The swap is also what closes the board's tail cleanly.
                      Closed hugs its content height (start-aligned, see
                      ClosedAreaView), so ending the strip with it left an empty
                      band running from under the collapsed header down to the
                      bottom of the board. AddColumnButton is a full-height lane,
                      so with it last the strip ends flush — the same property
                      SE-5396 relies on to keep a gap from trailing the columns.

                      Nothing about either control's behaviour changes here: only
                      which one comes first. */}
                  {onReopenCard ? (
                    <ClosedArea
                      closedGroups={closedGroups ?? []}
                      workingStages={workingStages ?? []}
                      canReopen={canEdit ?? false}
                      onReopen={onReopenCard}
                    />
                  ) : null}

                  {/* Add Column Button - Only visible to Owners and Editors */}
                  {canEdit && <AddColumnButton onPress={onAddColumn} />}
                </ScrollView>
              </Box>
            )}

            {/*
             * SE-5494: the right-side player panel used to render HERE, inline
             * in the board row. It is now the PlayerPreviewDrawer — an overlay
             * mounted at the screen root in MainContainer, alongside the
             * stage-move prompt and the share sheet — so it no longer competes
             * with the columns for horizontal space.
             */}
          </Stack>
        </Box>
      </ActiveDragProvider>

      {/* @dnd-kit DragOverlay - rendered outside ActiveDragProvider so the overlay
          card does not inherit activeDragId from context and hide itself via isBeingDragged */}
      <DragOverlay
        /*
         * SE-6554 item N — RE-EXAMINED AND DELIBERATELY LEFT NULL.
         *
         * A drop animation flies the overlay from the cursor to where dnd-kit
         * measures the source draggable node to be. dnd-kit takes that
         * measurement as the drag ends, while the card's arrival in its new lane
         * comes from an Apollo optimistic write in the SAME commit — so what the
         * animation flies toward is a race, and when it loses, the card is drawn
         * for ~200ms at the position it just left. That is the flash this was
         * set to null for, and nothing in item N changes the timing: the gap the
         * lane opens is a placeholder, so the overlay still travels the full
         * lane-to-lane distance at drop. The premise for reviving it — a
         * near-zero end-of-drag delta — would only hold if the card itself moved
         * between columns mid-drag, which it deliberately does not.
         *
         * What removes the abruptness instead: the gap is already exactly the
         * card's height at exactly the card's slot, so the real card replaces it
         * with no reflow at all. Revisit only alongside a mechanism that makes
         * the settle position deterministic rather than raced.
         */
        dropAnimation={null}
        // eslint-disable-next-line no-restricted-syntax -- dnd-kit DragOverlay library style API; supra-token overlay z-index + pointer passthrough (no closed token)
        style={DRAG_OVERLAY_STYLE}
      >
        {/*
         * EVERY card drag renders an overlay, rank-reorder included.
         *
         * This used to exclude `dragType === "rank-reorder"`, on the premise
         * that a rank drag "moves the card in place via the sortable transform"
         * and therefore needs no overlay. That premise does not hold on this
         * board, and the result was a drag with NOTHING on screen following the
         * pointer — with Rank as the default sort, that is most drags.
         *
         * Why the transform is not there to move it (@dnd-kit/sortable 10.0.0,
         * useSortable): `finalTransform = displaceItem ? … : null`, and
         * `displaceItem` requires `isValidIndex(overIndex)` where
         * `overIndex = items.indexOf(over.id)` — `items` being ONLY the ids in
         * that card's own SortableContext, i.e. its own column. This board
         * resolves `over` with `closestCorners` across every droppable, so as
         * soon as the pointer is over a stage column (`column-…`) or a card in
         * another lane — the whole of any cross-lane journey — `overIndex` is
         * -1, the transform is null, and the source card does not move at all.
         * Pair that with a suppressed overlay and the drag is invisible.
         *
         * Rendering the overlay also sets dnd-kit's own `useDragOverlay` flag
         * (`dragOverlay.rect !== null`), which tells useSortable to stop
         * displacing the drag source — so the origin card staying put is now
         * the library's intent rather than an accident, and it is exactly what
         * Figma 80:16392 asks for: origin at 40%, ghost under the cursor.
         */}
        {active &&
        active.data.current &&
        active.data.current.type !== "column" ? (
          // Render different overlay based on drag source
          active.data.current.source === "sidebar" ? ( // Compact sidebar-style overlay for sidebar drags
            <SidebarDragCard
              playerName={active.data.current.playerName}
              contractValue={active.data.current.contractValue}
              contractMonths={active.data.current.contractMonths}
              teamName={active.data.current.teamName}
              age={active.data.current.age}
              archetype={active.data.current.archetype}
              gpr={active.data.current.gpr}
            /> // Full kanban-style overlay for kanban drags
          ) : (
            /* Slightly transparent overlay. @dnd-kit's DragOverlay owns all
               POSITIONING (it transforms its own wrapper element); the transform
               here is a separate, purely cosmetic lift on the inner card, so the
               two never compete. */
            <Box className={DRAG_OVERLAY_CARD_CLASS}>
              <KanbanPlayerCard
                playerId={
                  active.data.current.id ??
                  String(active.id).replace(/^kanban-/, "")
                }
                playerName={active.data.current.playerName}
                archetype={active.data.current.archetype}
                age={active.data.current.age}
                gpr={active.data.current.gpr}
                contractValue={active.data.current.contractValue}
                contractMonths={active.data.current.contractMonths}
                agentName="" // Not needed for overlay
                teamName={active.data.current.teamName}
                boardId={boardId}
                initialTags={[]} // Simplified for overlay
                onPress={noopPress} // No interaction in overlay
                // The overlay is a picture, not a drag target. Without this it
                // registers a draggable under `kanban-<playerId>` — the id the
                // card being dragged is already registered under — and the
                // second registration would take over the node ref dnd-kit
                // measures mid-drag. Harmless-looking until it is not.
                isDragActivationDisabled
              />
            </Box>
          )
        ) : null}
      </DragOverlay>
    </DndContext>
  </Box>
);

MainView.displayName = "MainView";

export default memo(MainView);
